<template>
  <div class="wholesaler-my-profile">
    <!-- 我的页面内容 -->
    <div class="my-content" style="margin-top: 60px;">
      <!-- 批发商信息 -->
      <div class="profile-info">
        <h2>{{ $t('wholesaler.MyProfile.title') }}</h2>
        <div v-if="wholesalerLoading" class="loading-text">{{ $t('common.loading') }}</div>
        <template v-else-if="wholesalerInfo">
          <p><strong>{{ $t('wholesaler.MyProfile.name') }}:</strong> {{ wholesalerInfo.companyName || wholesalerInfo.wholesalerCode || $t('wholesaler.MyProfile.unnamed') }}</p>
          <p><strong>{{ $t('wholesaler.MyProfile.contact') }}:</strong> {{ wholesalerInfo.contactPerson || $t('wholesaler.MyProfile.notSet') }}</p>
          <p><strong>{{ $t('wholesaler.MyProfile.contactPhone') }}:</strong> {{ wholesalerInfo.contactPhone || $t('wholesaler.MyProfile.notSet') }}</p>
          <p><strong>{{ $t('wholesaler.MyProfile.email') }}:</strong> {{ wholesalerInfo.email || $t('wholesaler.MyProfile.notSet') }}</p>
          <p><strong>{{ $t('wholesaler.MyProfile.address') }}:</strong> {{ wholesalerInfo.address || $t('wholesaler.MyProfile.notSet') }}</p>
          <p v-if="wholesalerInfo.description"><strong>{{ $t('wholesaler.MyProfile.description') }}:</strong> {{ wholesalerInfo.description }}</p>
        </template>
        <div v-else class="empty-text">{{ $t('wholesaler.MyProfile.noInfo') }}</div>
      </div>

      <!-- 功能菜单 -->
      <div class="function-menu">
        <ul>
          <li @click="navigateTo('productManagement')">{{ $t('wholesaler.MyProfile.productManagement') }}</li>
          <li @click="navigateTo('orderManagement')">{{ $t('wholesaler.MyProfile.orderManagement') }}</li>
          <li @click="navigateTo('deliveryManagement')">{{ $t('wholesaler.MyProfile.deliveryManagement') }}</li>
          <li @click="navigateTo('driverManagement')">{{ $t('wholesaler.MyProfile.driverManagement') }}</li>
          <li @click="goToStallManagement">{{ $t('wholesaler.MyProfile.stallManagement') }}</li>
          <li @click="goToShopInfo">{{ $t('wholesaler.MyProfile.shopInfo') }}</li>
          <li @click="goToWarehouse">{{ $t('wholesaler.MyProfile.warehouseManagement') }}</li>
          <li @click="goToSupplierManagement">{{ $t('wholesaler.MyProfile.supplierManagement') }}</li>
          <li @click="goToPurchaseManagement">{{ $t('wholesaler.MyProfile.purchaseManagement') }}</li>
          <li @click="goToPrinterConfig">{{ $t('wholesaler.MyProfile.printerSettings') }}</li>
          <li @click="goToReceiptPrintConfig">{{ $t('wholesaler.MyProfile.receiptPrinterSettings') }}</li>
          <li @click="showChangePassword = true">{{ $t('wholesaler.MyProfile.changePassword') }}</li>
          <li @click="handleLogout">{{ $t('wholesaler.MyProfile.logout') }}</li>
        </ul>
      </div>

      <!-- 删除账号 -->
      <div class="delete-account-section">
        <button class="btn-delete-account" @click="showDeleteAccount = true">{{ $t('common.deleteAccount') || '删除账号' }}</button>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('navigation.home') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('products')">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('navigation.products') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('wholesale')">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('navigation.wholesale') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('navigation.orders') }}</span>
      </button>
      <button class="nav-item active">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('navigation.profile') }}</span>
      </button>
    </nav>

    <!-- 修改密码弹窗 -->
    <ChangePasswordModal :visible.sync="showChangePassword" />

    <!-- 删除账号弹窗 -->
    <div class="delete-account-modal" v-if="showDeleteAccount">
      <div class="modal-overlay" @click="showDeleteAccount = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>{{ $t('common.confirmDelete') || '确认删除' }}</h3>
            <button class="close-btn" @click="showDeleteAccount = false">×</button>
          </div>
          <div class="modal-body">
            <p class="warning-text">{{ $t('wholesaler.MyProfile.deleteAccountWarning') || '删除账号后，以下数据将被清除：' }}</p>
            <ul class="warning-list">
              <li>{{ $t('wholesaler.MyProfile.deleteWarning1') || '您的登录信息' }}</li>
              <li>{{ $t('wholesaler.MyProfile.deleteWarning2') || '店铺信息和商品数据' }}</li>
              <li>{{ $t('wholesaler.MyProfile.deleteWarning3') || '订单记录和交易历史' }}</li>
              <li>{{ $t('wholesaler.MyProfile.deleteWarning4') || '所有关联数据' }}</li>
            </ul>
            <p class="warning-text danger">{{ $t('wholesaler.MyProfile.deleteAccountIrreversible') || '此操作不可恢复！' }}</p>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="showDeleteAccount = false">{{ $t('common.cancel') || '取消' }}</button>
            <button class="btn-confirm-danger" @click="confirmDeleteAccount">{{ $t('common.confirmDelete') || '确认删除' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ChangePasswordModal from '@/components/ChangePasswordModal.vue';
import api from '@/services/api';

export default {
  name: 'WholesalerMyProfile',
  components: { ChangePasswordModal },
  data() {
    return {
      wholesalerInfo: null,
      wholesalerLoading: false,
      showChangePassword: false,
      showDeleteAccount: false
    };
  },
  computed: {
    ...mapGetters(['userName'])
  },
  async mounted() {
    await this.loadWholesalerInfo();
  },
  methods: {
    async loadWholesalerInfo() {
      this.wholesalerLoading = true;
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) {
          console.warn('User ID not found');
          this.wholesalerLoading = false;
          return;
        }
        const res = await api.get(`/wholesalers/by-user/${userId}`);
        this.wholesalerInfo = res.data || res || null;
      } catch (error) {
        console.error('Failed to load wholesaler info:', error);
        this.wholesalerInfo = null;
      } finally {
        this.wholesalerLoading = false;
      }
    },

    navigateTo(tab) {
      // 底部导航
      const bottomNavRoutes = {
        home: '/wholesaler/home',
        products: '/wholesaler/product',
        wholesale: '/wholesaler/wholesale-price-list',
        orders: '/wholesaler/orders'
      };
      // 功能菜单
      const menuRoutes = {
        productManagement: '/wholesaler/product',
        orderManagement: '/wholesaler/orders',
        deliveryManagement: '/wholesaler/delivery-tasks',
        driverManagement: '/wholesaler/driver-management',
        accountSettings: '/profile'
      };
      const targetRoute = bottomNavRoutes[tab] || menuRoutes[tab];
      if (targetRoute) {
        this.$router.push(targetRoute);
      } else {
        this.$message.info(this.$t('wholesaler.MyProfile.developing'));
      }
    },

    goToShopInfo() {
      this.$router.push({ name: 'ShopInfo' });
    },

    goToWarehouse() {
      this.$router.push({ name: 'Warehouse' });
    },

    goToPrinterConfig() {
      this.$router.push({ name: 'PrinterConfig' });
    },

    goToReceiptPrintConfig() {
      this.$router.push({ name: 'ReceiptPrintConfig' });
    },

    goToStallManagement() {
      this.$router.push('/wholesaler/stalls');
    },

    goToSupplierManagement() {
      this.$router.push('/wholesaler/supplier-list');
    },

    goToPurchaseManagement() {
      this.$router.push('/wholesaler/purchase-list');
    },

    handleLogout() {
      if (confirm(this.$t('wholesaler.MyProfile.confirmLogout'))) {
        this.$store.dispatch('auth/logout');
        this.$router.replace('/login');
      }
    },

    async confirmDeleteAccount() {
      try {
        const res = await api.delete('/auth/account');
        if (res !== undefined && res !== null) {
          this.$message.success(this.$t('wholesaler.MyProfile.accountDeleted') || '账号已删除');
          this.showDeleteAccount = false;
          // 清除本地存储
          localStorage.clear();
          sessionStorage.clear();
          // 跳转到登录页
          this.$router.replace('/login');
        } else {
          this.$message.error(this.$t('wholesaler.MyProfile.deleteFailed') || '删除失败');
        }
      } catch (error) {
        console.error('删除账号失败:', error);
        this.$message.error('删除失败：' + (error.response?.data?.message || error.message));
      }
    }
  }
};
</script>

<style scoped>
.wholesaler-my-profile {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 60px;
}

.my-content {
  padding: 12px;
}

.profile-info {
  margin-bottom: 20px;
}

.profile-info h2 {
  font-size: 18px;
  margin-bottom: 8px;
}

.profile-info p {
  font-size: 14px;
  margin-bottom: 4px;
}

.profile-info .loading-text,
.profile-info .empty-text {
  color: #999;
  font-size: 14px;
}

.function-menu ul {
  list-style-type: none;
  padding: 0;
}

.function-menu li {
  padding: 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.function-menu li:active {
  transform: scale(0.98);
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

/* ==================== 删除账号 ==================== */
.delete-account-section {
  padding: 12px 0 24px;
}

.btn-delete-account {
  width: 100%;
  padding: 12px;
  background: none;
  color: #bbb;
  border: none;
  font-size: 13px;
  cursor: pointer;
}

.btn-delete-account:active {
  color: #999;
}

/* ==================== Modal ==================== */
.delete-account-modal .modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  opacity: 1 !important;
  visibility: visible !important;
}

.delete-account-modal .modal-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.delete-account-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.delete-account-modal .modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.delete-account-modal .close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
}

.delete-account-modal .modal-body {
  padding: 20px;
}

.delete-account-modal .warning-text {
  font-size: 14px;
  color: #666;
  margin: 8px 0;
  line-height: 1.5;
}

.delete-account-modal .warning-text.danger {
  color: #f5576c;
  font-weight: bold;
}

.delete-account-modal .warning-list {
  margin: 8px 0;
  padding-left: 0;
  list-style: none;
}

.delete-account-modal .warning-list li {
  font-size: 14px;
  color: #666;
  margin: 4px 0;
}

.delete-account-modal .modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.delete-account-modal .btn-cancel {
  flex: 1;
  padding: 12px;
  background: #f0f0f0;
  color: #666;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.delete-account-modal .btn-confirm-danger {
  flex: 1;
  padding: 12px;
  background: #f5576c;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
}

.delete-account-modal .btn-confirm-danger:active {
  background: #e04858;
}

</style>
